// src/Services/brandService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

export interface Brand {
  id?: string;
  name: string;
  description: string;
  imageUrl: string;
  slug: string;
  isActive: boolean;
  featured: boolean;
  websiteUrl?: string;
  createdBy: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

const BRANDS_COLLECTION = 'brands';

export class BrandService {
  // Get all brands
  static async getAllBrands(): Promise<Brand[]> {
    try {
      const brandsRef = collection(db, BRANDS_COLLECTION);
      const q = query(brandsRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Brand;
      });
    } catch (error) {
      console.error('Error fetching all brands:', error);
      throw new Error('Failed to fetch brands');
    }
  }

  // Get only active brands
  static async getActiveBrands(): Promise<Brand[]> {
    try {
      const brandsRef = collection(db, BRANDS_COLLECTION);
      const q = query(
        brandsRef,
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Brand;
      });
    } catch (error) {
      console.error('Error fetching active brands:', error);
      throw new Error('Failed to fetch active brands');
    }
  }

  // Get only featured active brands
  static async getFeaturedBrands(): Promise<Brand[]> {
    try {
      const brandsRef = collection(db, BRANDS_COLLECTION);
      const q = query(
        brandsRef,
        where('isActive', '==', true),
        where('featured', '==', true),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Brand;
      });
    } catch (error) {
      console.error('Error fetching featured brands:', error);
      throw new Error('Failed to fetch featured brands');
    }
  }

  // Get a single brand by ID
  static async getBrandById(id: string): Promise<Brand | null> {
    try {
      const brandRef = doc(db, BRANDS_COLLECTION, id);
      const brandSnap = await getDoc(brandRef);

      if (brandSnap.exists()) {
        const data = brandSnap.data();
        return {
          id: brandSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Brand;
      }

      return null;
    } catch (error) {
      console.error('Error fetching brand by ID:', error);
      throw new Error('Failed to fetch brand');
    }
  }

  // Get a single brand by slug
  static async getBrandBySlug(slug: string): Promise<Brand | null> {
    try {
      const brandsRef = collection(db, BRANDS_COLLECTION);
      const q = query(brandsRef, where('slug', '==', slug));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Brand;
      }

      return null;
    } catch (error) {
      console.error('Error fetching brand by slug:', error);
      throw new Error('Failed to fetch brand by slug');
    }
  }
}